const embedCssCache = new Map<string, Promise<string>>();

function cacheKey(family: string, weights: number[]): string {
  const sorted = [...weights].sort((a, b) => a - b);
  return `${family.toLowerCase()}:${sorted.join(',')}`;
}

function buildGoogleFontsCssUrl(family: string, weights: number[]): string {
  const sorted = [...weights].sort((a, b) => a - b);
  const familyParam = encodeURIComponent(family);
  const wghtParam = sorted.join(';');
  return `https://fonts.googleapis.com/css2?family=${familyParam}:wght@${wghtParam}&display=swap`;
}

async function fetchAsDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache',
    });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return await new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const urlInCssRegex = /url\(["']?([^"')]+)["']?\)/g;

async function embedRemoteUrlsInCss(
  cssText: string,
  baseUrl: string,
): Promise<string> {
  const matches = [...cssText.matchAll(urlInCssRegex)];
  if (matches.length === 0) {
    return cssText;
  }

  let result = cssText;
  const seen = new Set<string>();

  for (const match of matches) {
    const rawUrl = match[1]?.trim();
    if (!rawUrl || seen.has(rawUrl)) {
      continue;
    }
    seen.add(rawUrl);

    let absoluteUrl = rawUrl;
    if (!rawUrl.startsWith('data:') && !rawUrl.startsWith('blob:')) {
      try {
        absoluteUrl = new URL(rawUrl, baseUrl).href;
      } catch {
        continue;
      }
    } else {
      continue;
    }

    const dataUrl = await fetchAsDataUrl(absoluteUrl);
    if (!dataUrl) {
      continue;
    }

    const escaped = rawUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(
      new RegExp(`url\\(["']?${escaped}["']?\\)`, 'g'),
      `url(${dataUrl})`,
    );
  }

  return result;
}

/**
 * Builds `@font-face` CSS with embedded font files via fetch (CORS-safe).
 * Avoids reading cross-origin stylesheet `cssRules` required by `getFontEmbedCSS`.
 */
export function buildGoogleFontEmbedCss(
  family: string,
  weights: number[],
): Promise<string> {
  const key = cacheKey(family, weights);
  const cached = embedCssCache.get(key);
  if (cached) {
    return cached;
  }

  const promise = (async () => {
    const cssUrl = buildGoogleFontsCssUrl(family, weights);
    const response = await fetch(cssUrl, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache',
      headers: {
        Accept: 'text/css,*/*',
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch Google Fonts CSS for ${family}`);
    }
    const cssText = await response.text();
    return embedRemoteUrlsInCss(cssText, cssUrl);
  })();

  embedCssCache.set(key, promise);
  return promise;
}
